import { Group, Line, Rect } from 'react-konva'
import { KonvaEventObject } from 'konva/lib/Node'
import { Wall2D, Window2D } from '../../../models/Construction'
import { useCallback, useState } from 'react'
import { BaseComponentProps } from './EditorWrapper'
import { FLOOR_PLAN_COLORS } from './Elements'

interface WindowComponentProps extends BaseComponentProps<Window2D> {
  wall: Wall2D
}

export const WindowComponent: React.FC<WindowComponentProps> = ({
  wall,
  element,
  scale,
  thicknessMultiplier = 1,
  isSelected = false,
  isHovered = false,
  onSelect,
}) => {
  const [isWindowHovered, setIsWindowHovered] = useState(isHovered)

  const handleClick = useCallback(
    (e: KonvaEventObject<MouseEvent>) => {
      e.cancelBubble = true
      onSelect?.(element)
    },
    [element, onSelect],
  )

  const getWindowColor = (type: Window2D['type']): string => {
    const colors: Record<Window2D['type'], string> = {
      BAY: '#4169E1',
      LARGE: '#1E90FF',
      PICTURE: '#00BFFF',
      SLIDING: '#87CEEB',
      SMALL: '#B0E0E6',
      HOPPER: '#ADD8E6',
      UNKNOWN: '#DCDCDC',
      CASEMENT: '#4682B4',
      AWNING: '#5F9EA0',
      BOW: '#6495ED',
      DOUBLE_HUNG: '#87CEFA',
      SKYLIGHT: '#B0C4DE',
    }
    return colors[type]
  }

  const centerX = wall.center_point[0] * scale
  const centerY = wall.center_point[1] * scale
  const angle = wall.angle || 0
  const radians = (angle * Math.PI) / 180

  const offsetFromCenter = element.center_location_on_wall - wall.length / 2
  const windowX = centerX + offsetFromCenter * Math.cos(radians) * scale
  const windowY = centerY + offsetFromCenter * Math.sin(radians) * scale

  const windowWidth = element.width * scale
  const windowHeight = wall.thickness * scale * thicknessMultiplier * 0.75

  return (
    <Group>
      <Rect
        x={windowX}
        y={windowY}
        width={windowWidth}
        height={windowHeight}
        offsetX={windowWidth / 2}
        offsetY={windowHeight / 2}
        fill={
          isSelected
            ? '#FFFFFF'
            : isWindowHovered
              ? '#FFFFFF'
              : FLOOR_PLAN_COLORS.WINDOWS.UNKNOWN
        }
        stroke={isSelected ? '#FF0000' : '#000000'}
        strokeWidth={isSelected ? 2 : isWindowHovered ? 2 : 1}
        rotation={angle}
        opacity={0.8}
        onClick={handleClick}
        onTap={handleClick}
        onMouseEnter={() => setIsWindowHovered(true)}
        onMouseLeave={() => setIsWindowHovered(false)}
      />

      <Rect
        x={angle === 90 ? windowX : windowX + 8}
        y={angle === 0 ? windowY : windowY + 8}
        width={windowWidth - 16}
        height={windowHeight}
        offsetX={windowWidth / 2}
        offsetY={windowHeight / 2}
        fill={
          isSelected
            ? '#FFFFFF'
            : isWindowHovered
              ? '#FFFFFF'
              : FLOOR_PLAN_COLORS.WINDOWS.LARGE
        }
        stroke={isSelected ? '#FF0000' : '#000000'}
        strokeWidth={isSelected ? 2 : isWindowHovered ? 2 : 1}
        rotation={angle}
        opacity={0.8}
        onClick={handleClick}
        onTap={handleClick}
        onMouseEnter={() => setIsWindowHovered(true)}
        onMouseLeave={() => setIsWindowHovered(false)}
      />

      <Rect
        x={windowX}
        y={windowY}
        width={4}
        height={windowHeight}
        offsetX={(windowWidth / 2) - 2}
        offsetY={windowHeight / 2}
        fill={
          isSelected
            ? '#FFFFFF'
            : isWindowHovered
              ? '#FFFFFF'
              : FLOOR_PLAN_COLORS.WINDOWS.LARGE
        }
        stroke={isSelected ? '#FF0000' : '#000000'}
        strokeWidth={isSelected ? 2 : isWindowHovered ? 2 : 1}
        rotation={angle}
        opacity={0.8}
        onClick={handleClick}
        onTap={handleClick}
        onMouseEnter={() => setIsWindowHovered(true)}
        onMouseLeave={() => setIsWindowHovered(false)}
      />

      <Rect
        x={windowX}
        y={windowY}
        width={4}
        height={windowHeight}
        offsetX={- (windowWidth / 2) + 6}
        offsetY={windowHeight / 2}
        fill={
          isSelected
            ? '#FFFFFF'
            : isWindowHovered
              ? '#FFFFFF'
              : FLOOR_PLAN_COLORS.WINDOWS.LARGE
        }
        stroke={isSelected ? '#FF0000' : '#000000'}
        strokeWidth={isSelected ? 2 : isWindowHovered ? 2 : 1}
        rotation={angle}
        opacity={0.8}
        onClick={handleClick}
        onTap={handleClick}
        onMouseEnter={() => setIsWindowHovered(true)}
        onMouseLeave={() => setIsWindowHovered(false)}
      />

      <Line
        points={[(-windowWidth / 2) + 8 , 0, (windowWidth / 2) - 8, 0]}
        x={windowX}
        y={windowY}
        stroke='#2F4F4F'
        strokeWidth={1}
        rotation={angle}
        listening={false}
      />
    </Group>
  )
}
